/**
 * Session storage shared by the React hooks and the non-React API helpers.
 *
 * Keeping the key and the read/write logic in one place is what lets `api.ts` send the same
 * bearer token the `useTensileAuth()` hook rendered with — previously the hook wrote
 * `tensile.auth.session` while the API client read a different key, so an authenticated user
 * could still get 401s.
 */

import type { AccountRole } from "./crypto.ts";

export const AUTH_STORAGE_KEY = "tensile.auth.session";

export interface TensileAuthSession {
  token: string;
  email: string | null;
  accountId: string | null;
  role: AccountRole | null;
}

export function readAuthSession(): TensileAuthSession | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) ?? "null") as Partial<
      TensileAuthSession
    > | null;
    if (!parsed?.token) return null;
    return {
      token: String(parsed.token),
      email: typeof parsed.email === "string" ? parsed.email : null,
      accountId: typeof parsed.accountId === "string" ? parsed.accountId : null,
      role:
        parsed.role === "owner" || parsed.role === "admin" || parsed.role === "run_only"
          ? parsed.role
          : null,
    };
  } catch {
    return null;
  }
}

export function writeAuthSession(session: TensileAuthSession): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
}

export function clearAuthSession(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(AUTH_STORAGE_KEY);
}
